import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Code,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import type { TFunction } from 'i18next'
import { FiCopy } from 'react-icons/fi'
import type { ContractRegistryRecord } from '@/types'
import { ChainBadge } from '@/components/common/ChainBadge'
import { StatusBadge } from '@/components/common/StatusBadge'
import { formatDateTime } from '@/utils/formatUtils'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'

interface RegistryDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  selectedRecord: ContractRegistryRecord | null
  isRefreshing?: boolean
  onRefresh?: () => Promise<unknown> | void
  getStatusDisplayName: (record: ContractRegistryRecord) => string
  resolveRegistryChainId: (record?: ContractRegistryRecord | null) => string
  getFormattedAmount: (record: ContractRegistryRecord) => { amountText: string; symbol: string }
  copyToClipboard: (text: string) => void
  textColor: string
  t: TFunction
}

const formatJsonForDisplay = (raw?: string | null): string | null => {
  if (!raw || raw.trim().length === 0) {
    return null
  }
  try {
    const parsed = JSON.parse(raw)
    return JSON.stringify(parsed, null, 2)
  } catch {
    return raw
  }
}

export const RegistryDetailsModal = ({
  isOpen,
  onClose,
  selectedRecord,
  isRefreshing = false,
  onRefresh,
  getStatusDisplayName,
  resolveRegistryChainId,
  getFormattedAmount,
  copyToClipboard,
  textColor,
  t,
}: RegistryDetailsModalProps) => {
  const canResumeToActive = selectedRecord?.status_id === 'orrs_on_hold'
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pr={12}>
          <HStack justify="space-between" spacing={3}>
            <Text>{t('contractRegistry.dialogs.details.title')}</Text>
            {onRefresh && (
              <RefreshIconButton
                label={t('common.refresh')}
                tooltipPlacement="left"
                onClick={() => void onRefresh()}
                isLoading={isRefreshing}
                isDisabled={isRefreshing}
                flexShrink={0}
              />
            )}
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {selectedRecord && (
            <VStack spacing={4} align="stretch">
              <HStack spacing={4}>
                <Box flex="1">
                  <Text fontSize="sm" fontWeight="medium" color={textColor} mb={1}>
                    {t('contractRegistry.table.orderId')}
                  </Text>
                  <Code>{selectedRecord.order_id}</Code>
                </Box>
                <Box flex="1">
                  <Text fontSize="sm" fontWeight="medium" color={textColor} mb={1}>
                    {t('contractRegistry.table.status')}
                  </Text>
                  <StatusBadge
                    statusType="registry"
                    status={selectedRecord.status_id || selectedRecord.status}
                    label={getStatusDisplayName(selectedRecord)}
                    size="lg"
                  />
                </Box>
              </HStack>

              <HStack spacing={4}>
                <Box flex="1">
                  <Text fontSize="sm" fontWeight="medium" color={textColor} mb={1}>
                    {t('contractRegistry.table.merchant')}
                  </Text>
                  <Text>{selectedRecord.merchant_name || t('common.unknown')}</Text>
                </Box>
                <Box flex="1">
                  <Text fontSize="sm" fontWeight="medium" color={textColor} mb={1}>
                    {t('contractRegistry.table.chain')}
                  </Text>
                  <ChainBadge chainId={resolveRegistryChainId(selectedRecord) || 'unknown'} />
                </Box>
              </HStack>

              <Box>
                <Text fontSize="sm" fontWeight="medium" color={textColor} mb={1}>
                  {t('contractRegistry.table.paymentAddress', 'Payment Address')}
                </Text>
                <HStack>
                  <Code flex="1">{selectedRecord.payment_address ?? t('common.na')}</Code>
                  <Tooltip label={t('contractRegistry.actions.copyAddress')}>
                    <IconButton
                      aria-label="Copy"
                      icon={<FiCopy />}
                      size="sm"
                      variant="ghost"
                      onClick={() => selectedRecord.payment_address
                        && copyToClipboard(selectedRecord.payment_address)}
                      isDisabled={!selectedRecord.payment_address}
                    />
                  </Tooltip>
                </HStack>
              </Box>

              <HStack spacing={4}>
                <Box flex="1">
                  <Text fontSize="sm" fontWeight="medium" color={textColor} mb={1}>
                    {t('contractRegistry.table.amount')}
                  </Text>
                  {(() => {
                    const { amountText, symbol } = getFormattedAmount(selectedRecord)
                    return (
                      <Text>
                        {amountText} {symbol}
                      </Text>
                    )
                  })()}
                </Box>
                <Box flex="1">
                  <Text fontSize="sm" fontWeight="medium" color={textColor} mb={1}>
                    {t('contractRegistry.dialogs.details.created')}
                  </Text>
                  <Text fontSize="sm">{formatDateTime(selectedRecord.created_at)}</Text>
                </Box>
              </HStack>

              {selectedRecord.deployment_tx_hash && (
                <Box>
                  <Text fontSize="sm" fontWeight="medium" color={textColor} mb={1}>
                    {t('contractRegistry.dialogs.details.deploymentTxHash')}
                  </Text>
                  <HStack>
                    <Code flex="1">{selectedRecord.deployment_tx_hash}</Code>
                    <Tooltip label={t('contractRegistry.actions.copyTxHash')}>
                      <IconButton
                        aria-label="Copy"
                        icon={<FiCopy />}
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(selectedRecord.deployment_tx_hash!)}
                      />
                    </Tooltip>
                  </HStack>
                </Box>
              )}

              {selectedRecord.error_message && (
                <Box>
                  <Text fontSize="sm" fontWeight="medium" color="red.500" mb={1}>
                    {t('contractRegistry.dialogs.details.errorMessage')}
                  </Text>
                  <Alert status="error">
                    <AlertIcon />
                    <Text fontSize="sm">{selectedRecord.error_message}</Text>
                  </Alert>
                </Box>
              )}

              <Box>
                <Text fontSize="sm" fontWeight="medium" color={textColor} mb={1}>
                  {t('contractRegistry.dialogs.details.deployTriggerLog')}
                </Text>
                <Box
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor="gray.200"
                  p={3}
                  maxHeight="260px"
                  overflowY="auto"
                  minHeight="40px"
                >
                  <Text fontSize="sm" whiteSpace="pre-wrap" wordBreak="break-word">
                    {formatJsonForDisplay(selectedRecord.deploy_trigger_log_json) ?? ''}
                  </Text>
                </Box>
              </Box>
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          {canResumeToActive && (
            <Text fontSize="sm" color={textColor} mr={3}>
              {t('contractRegistry.actions.resumeManagedByOrder')}
            </Text>
          )}
          <Button onClick={onClose}>{t('common.close')}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
